// Escapa fórmulas (=, +, -, @, \t, \r) para prevenir CSV/Excel formula injection.
function safeCell(v: unknown): string | number | boolean | Date {
  if (v == null) return "";
  if (typeof v === "number" || typeof v === "boolean") return v;
  if (v instanceof Date) return v;
  const s = String(v);
  if (/^[=+\-@\t\r]/.test(s)) return "'" + s;
  return s;
}

export type ExportColumn<T> = {
  key: keyof T | string;
  label: string;
  get?: (row: T) => unknown;
};

export type ExportMetadata = {
  reportTitle?: string;
  institution?: string;
  rangeFrom?: string;
  rangeTo?: string;
  generatedBy?: string;
};

export async function exportToExcel<T>(
  rows: T[],
  columns: ExportColumn<T>[],
  filename: string,
  metadata?: ExportMetadata
): Promise<void> {
  const XLSX = await import("xlsx");

  const meta = {
    reportTitle: "FOCA — Historial de valoraciones",
    institution: "Fundación Oftalmológica del Caribe",
    generatedBy: metadata?.generatedBy || "",
    ...metadata,
  };

  const rangeText = meta.rangeFrom || meta.rangeTo
    ? `Rango: ${meta.rangeFrom || "(inicio)"} — ${meta.rangeTo || "(fin)"}`
    : "Rango: histórico completo";

  const metaRows: (string | number)[][] = [
    [meta.reportTitle],
    [meta.institution],
    [rangeText],
    [`Generado: ${new Date().toLocaleString("es-CO")}`],
    meta.generatedBy ? [`Por: ${meta.generatedBy}`] : [""],
    [`Total registros: ${rows.length}`],
    [""],
  ];

  const header = columns.map((c) => c.label);
  const body = rows.map((row) =>
    columns.map((c) => safeCell(c.get ? c.get(row) : (row as Record<string, unknown>)[c.key as string]))
  );

  const ws = XLSX.utils.aoa_to_sheet([...metaRows, header, ...body]);

  // Anchos de columna
  ws["!cols"] = columns.map(() => ({ wch: 22 }));

  // Merge del título en la primera fila (todas las columnas)
  const lastCol = columns.length - 1;
  ws["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: lastCol } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: lastCol } },
    { s: { r: 3, c: 0 }, e: { r: 3, c: lastCol } },
    { s: { r: 5, c: 0 }, e: { r: 5, c: lastCol } },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Datos");
  const safeName = filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`;
  XLSX.writeFile(wb, safeName);
}
